import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import {
  TranslationKey,
  TranslationsResponse,
  Language,
  LanguagesResponse,
  CreateTranslationRequest,
  UpdateTranslationRequest,
  TranslationQueryParams,
  ApplicationType,
  BulkImportValidateResponse,
  BulkImportResult,
  ImportMode,
  PublishStatus,
  PublishResult,
  TranslationVersion,
  VersionsResponse,
  S3Environment,
  VersionQueryParams,
  RollbackResult,
  ExportResponse,
  LegacyImportValidateResponse,
  LegacyJsonValidateRequest,
  LegacyJsonImportRequest,
  LegacyExcelValidateRequest,
  LegacyExcelImportRequest,
  TranslationImportStartResponse,
  TranslationImportStatusResponse,
  PreviewInfoResponse,
  CopyLanguageValuesResult,
} from '../types/translation-types';

export class TranslationApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeadersWithOperator(): Promise<Record<string, string>> {
    const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      console.error('TranslationApi: No operator selected, preventing API call');
      throw new Error('No operator selected. Please select an operator first.');
    }

    const headers = await this.tokenManager.getAuthHeaders();

    return {
      'Content-Type': 'application/json',
      ...headers,
      'X-Operator-Id': operatorId.toString(),
    };
  }

  /**
   * Build query string from params
   * NOTE: Uses skip/take (not OData $skip/$top)
   */
  private buildQueryString(params: TranslationQueryParams): string {
    const queryParts: string[] = [];

    queryParts.push(`applicationType=${params.applicationType}`);

    if (params.skip !== undefined) {
      queryParts.push(`skip=${params.skip}`);
    }

    if (params.top !== undefined) {
      queryParts.push(`take=${params.top}`);
    }

    if (params.filter) {
      queryParts.push(`searchTerm=${encodeURIComponent(params.filter)}`);
    }

    return queryParts.join('&');
  }

  /**
   * Get translations list (paginated)
   * API: GET /api/v1/translations?applicationType={1-6}&searchTerm={string}&skip={int}&take={int}
   */
  async getTranslations(params: TranslationQueryParams): Promise<TranslationsResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const queryString = this.buildQueryString(params);
    const url = `${this.baseUrl}/translations?${queryString}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      '/translations'
    );

    const data = await response.json();

    // Handle various response formats:
    // 1. { count, rows } - Current API format
    // 2. { isSuccess, items, totalCount } - Documented format
    // 3. Array - Legacy format
    // 4. { value, '@odata.count' } - OData format

    // Current API format: { count, rows }
    if (data.rows !== undefined) {
      return {
        value: data.rows,
        '@odata.count': data.count ?? data.rows.length,
      };
    }

    // Documented format: { isSuccess, items, totalCount }
    if (data.items !== undefined) {
      return {
        value: data.items,
        '@odata.count': data.totalCount ?? data.items.length,
      };
    }

    // OData format: { value, '@odata.count' }
    if (data.value !== undefined) {
      return {
        value: data.value,
        '@odata.count': data['@odata.count'] ?? data.value.length,
      };
    }

    // Array format (legacy)
    if (Array.isArray(data)) {
      return {
        value: data,
        '@odata.count': data.length,
      };
    }

    // Fallback - return empty
    return {
      value: [],
      '@odata.count': 0,
    };
  }

  /**
   * Get a single translation by ID
   */
  async getById(id: number): Promise<TranslationKey> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/${id}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      `/translations/${id}`
    );

    return response.json();
  }

  /**
   * Create a new translation key with values
   */
  async create(data: CreateTranslationRequest): Promise<TranslationKey> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      },
      'Translation API',
      '/translations'
    );

    const result = await response.json();

    // Check for API-level validation errors
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to create translation');
    }

    return result;
  }

  /**
   * Update an existing translation
   */
  async update(id: number, data: UpdateTranslationRequest): Promise<TranslationKey> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/${id}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      },
      'Translation API',
      `/translations/${id}`
    );

    const result = await response.json();

    // Check for API-level validation errors
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to update translation');
    }

    return result;
  }

  /**
   * Bulk-copy every translation value from a base language to a newly
   * created compound-coded language (e.g. "he" -> "he#paz")
   */
  async copyLanguageValues(
    newLanguageCode: string,
    baseLanguageCode: string
  ): Promise<CopyLanguageValuesResult> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/languages/${encodeURIComponent(
      newLanguageCode
    )}/copy-from/${encodeURIComponent(baseLanguageCode)}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
      },
      'Translation API',
      `/translations/languages/${newLanguageCode}/copy-from/${baseLanguageCode}`
    );

    const result = await response.json();

    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to copy language values');
    }

    return result;
  }

  /**
   * Delete a translation key
   */
  async delete(id: number): Promise<void> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/${id}`;

    await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'DELETE',
        headers,
      },
      'Translation API',
      `/translations/${id}`
    );
  }

  /**
   * Get all available languages
   */
  async getLanguages(): Promise<LanguagesResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/languages`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      '/languages'
    );

    const data = await response.json();

    // Handle different response formats
    const isArray = Array.isArray(data);
    const items = isArray ? data : (data.rows || data.value || []);

    return {
      value: items,
      '@odata.count': isArray ? items.length : (data['@odata.count'] ?? data.count ?? items.length),
    };
  }

  // ============================================================================
  // Bulk Import Methods
  // ============================================================================

  /**
   * Validate a bulk import file and get preview
   */
  async validateBulkImport(
    applicationType: ApplicationType,
    file: File
  ): Promise<BulkImportValidateResponse> {
    const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      throw new Error('No operator selected. Please select an operator first.');
    }

    const authHeaders = await this.tokenManager.getAuthHeaders();
    const url = `${this.baseUrl}/translations/import/validate?applicationType=${applicationType}`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers: {
          ...authHeaders,
          'X-Operator-Id': operatorId.toString(),
          // Note: Don't set Content-Type for FormData, browser will set it with boundary
        },
        body: formData,
      },
      'Translation API',
      '/translations/import/validate'
    );

    return response.json();
  }

  /**
   * Execute bulk import after validation
   */
  async bulkImport(
    applicationType: ApplicationType,
    file: File,
    mode: ImportMode
  ): Promise<BulkImportResult> {
    const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      throw new Error('No operator selected. Please select an operator first.');
    }

    const authHeaders = await this.tokenManager.getAuthHeaders();
    const url = `${this.baseUrl}/translations/import?applicationType=${applicationType}&mode=${mode}`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers: {
          ...authHeaders,
          'X-Operator-Id': operatorId.toString(),
        },
        body: formData,
      },
      'Translation API',
      '/translations/import'
    );

    return response.json();
  }

  /**
   * Download Excel template for bulk import
   * Returns JSON with base64-encoded file content
   */
  async downloadTemplate(applicationType: ApplicationType): Promise<ExportResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/export/template?applicationType=${applicationType}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers: {
          ...headers,
          'Accept': 'text/plain',
        },
      },
      'Translation API',
      '/translations/export/template'
    );

    return response.json();
  }

  /**
   * Export all translations as Excel file
   * Returns JSON with base64-encoded file content
   */
  async exportTranslations(applicationType: ApplicationType): Promise<ExportResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/export?applicationType=${applicationType}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers: {
          ...headers,
          'Accept': 'text/plain',
        },
      },
      'Translation API',
      '/translations/export'
    );

    return response.json();
  }

  // ============================================================================
  // Publish & Version Methods
  // ============================================================================

  /**
   * Get publish status for an application type
   */
  async getPublishStatus(applicationType: ApplicationType): Promise<PublishStatus> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/publish-status/${applicationType}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      `/translations/publish-status/${applicationType}`
    );

    return response.json();
  }

  /**
   * Get preview of what will be published (raw JSON)
   * NOTE: applicationType is a PATH parameter
   */
  async getPublishPreview(applicationType: ApplicationType): Promise<string> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/preview/${applicationType}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      `/translations/preview/${applicationType}`
    );

    return response.text();
  }

  /**
   * Publish translations to S3 for a specific environment
   * NOTE: applicationType is a PATH parameter, NOT in the request body
   */
  async publish(
    applicationType: ApplicationType,
    environment: S3Environment,
    notes?: string
  ): Promise<PublishResult> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/publish/${applicationType}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          environment,
          notes,
        }),
      },
      'Translation API',
      `/translations/publish/${applicationType}`
    );

    const result = await response.json();

    // Check for API-level errors (HTTP 200 but isSuccess: false)
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to publish translations');
    }

    return result;
  }

  /**
   * Get version history for an application type
   */
  async getVersions(params: VersionQueryParams): Promise<VersionsResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const queryParts: string[] = [`applicationType=${params.applicationType}`];
    if (params.environment !== undefined) {
      queryParts.push(`environment=${params.environment}`);
    }

    const url = `${this.baseUrl}/translations/versions?${queryParts.join('&')}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      '/translations/versions'
    );

    const data = await response.json();

    // Handle different response formats
    const isArray = Array.isArray(data);
    const items = isArray ? data : (data.rows || data.value || []);

    return {
      value: items,
      '@odata.count': isArray ? items.length : (data['@odata.count'] ?? data.count ?? items.length),
    };
  }

  /**
   * Get a specific version by ID
   */
  async getVersionById(versionId: number): Promise<TranslationVersion> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/versions/${versionId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      `/translations/versions/${versionId}`
    );

    return response.json();
  }

  /**
   * Rollback to a previous version
   */
  async rollbackVersion(versionId: number, reason: string): Promise<RollbackResult> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/versions/${versionId}/rollback`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason }),
      },
      'Translation API',
      `/translations/versions/${versionId}/rollback`
    );

    return response.json();
  }

  // ============================================================================
  // Legacy Import Methods
  // ============================================================================

  /**
   * Validate a legacy JSON import file
   */
  async validateLegacyJsonImport(
    request: LegacyJsonValidateRequest
  ): Promise<LegacyImportValidateResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/import/json/validate`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      },
      'Translation API',
      '/translations/import/json/validate'
    );

    return response.json();
  }

  /**
   * Execute legacy JSON import (async - returns immediately with importId)
   */
  async legacyJsonImport(
    request: LegacyJsonImportRequest
  ): Promise<TranslationImportStartResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/import/json`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      },
      'Translation API',
      '/translations/import/json'
    );

    return response.json();
  }

  /**
   * Validate a legacy Excel import file (supports multi-sheet)
   */
  async validateLegacyExcelImport(
    request: LegacyExcelValidateRequest
  ): Promise<LegacyImportValidateResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/import/validate`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      },
      'Translation API',
      '/translations/import/validate'
    );

    return response.json();
  }

  /**
   * Execute legacy Excel import (async - returns immediately with importId)
   */
  async legacyExcelImport(
    request: LegacyExcelImportRequest
  ): Promise<TranslationImportStartResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/import`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      },
      'Translation API',
      '/translations/import'
    );

    return response.json();
  }

  /**
   * Get import status (for polling async imports)
   */
  async getImportStatus(
    importId: string
  ): Promise<TranslationImportStatusResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/import/status/${importId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      `/translations/import/status/${importId}`
    );

    return response.json();
  }

  /**
   * Execute JSON import with SSE streaming for real-time progress updates
   * @param request - The import request with file content and options
   * @param onProgress - Callback called with progress updates
   * @param abortSignal - Optional AbortSignal for cancellation
   * @returns Promise that resolves when import completes
   */
  async legacyJsonImportStream(
    request: LegacyJsonImportRequest,
    onProgress: (progress: TranslationImportStatusResponse, eventType: string) => void,
    abortSignal?: AbortSignal
  ): Promise<TranslationImportStatusResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/import/json/stream`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(request),
      signal: abortSignal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Import failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    if (!response.body) {
      throw new Error('Response body is null - SSE streaming not supported');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let lastProgress: TranslationImportStatusResponse | null = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let eventName = 'progress';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventName = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6);
          } else if (line === '' && eventData) {
            // End of event - process it
            try {
              const progress = JSON.parse(eventData) as TranslationImportStatusResponse;
              lastProgress = progress;
              onProgress(progress, eventName);
            } catch (parseError) {
              console.error('Failed to parse SSE event data:', parseError, eventData);
            }
            eventName = 'progress';
            eventData = '';
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (!lastProgress) {
      throw new Error('No progress data received from SSE stream');
    }

    return lastProgress;
  }

  /**
   * Execute Excel import with SSE streaming for real-time progress updates
   * @param request - The import request with file content and options
   * @param onProgress - Callback called with progress updates
   * @param abortSignal - Optional AbortSignal for cancellation
   * @returns Promise that resolves when import completes
   */
  async legacyExcelImportStream(
    request: LegacyExcelImportRequest,
    onProgress: (progress: TranslationImportStatusResponse, eventType: string) => void,
    abortSignal?: AbortSignal
  ): Promise<TranslationImportStatusResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/import/stream`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(request),
      signal: abortSignal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Import failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    if (!response.body) {
      throw new Error('Response body is null - SSE streaming not supported');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let lastProgress: TranslationImportStatusResponse | null = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let eventName = 'progress';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventName = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6);
          } else if (line === '' && eventData) {
            // End of event - process it
            try {
              const progress = JSON.parse(eventData) as TranslationImportStatusResponse;
              lastProgress = progress;
              onProgress(progress, eventName);
            } catch (parseError) {
              console.error('Failed to parse SSE event data:', parseError, eventData);
            }
            eventName = 'progress';
            eventData = '';
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (!lastProgress) {
      throw new Error('No progress data received from SSE stream');
    }

    return lastProgress;
  }

  /**
   * Helper: Convert file to base64
   */
  static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/json;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  // ============================================================================
  // Preview Methods
  // ============================================================================

  /**
   * Get preview info (metadata about available files)
   * GET /api/v1/translations/preview/{applicationType}/info
   */
  async getPreviewInfo(applicationType: ApplicationType): Promise<PreviewInfoResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/translations/preview/${applicationType}/info`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      `/translations/preview/${applicationType}/info`
    );

    return response.json();
  }

  /**
   * Get preview JSON content
   * GET /api/v1/translations/preview/{applicationType}?fileKey={key}
   * For single-file apps, fileKey is optional
   */
  async getPreviewContent(applicationType: ApplicationType, fileKey?: string): Promise<string> {
    const headers = await this.getAuthHeadersWithOperator();
    let url = `${this.baseUrl}/translations/preview/${applicationType}`;
    if (fileKey) {
      url += `?fileKey=${encodeURIComponent(fileKey)}`;
    }

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Translation API',
      `/translations/preview/${applicationType}`
    );

    return response.text();
  }

  /**
   * Get the download URL for preview file
   * Returns the URL that can be used to download the file
   */
  getPreviewDownloadUrl(applicationType: ApplicationType, fileKey?: string): string {
    let url = `${this.baseUrl}/translations/preview/${applicationType}/download`;
    if (fileKey) {
      url += `?fileKey=${encodeURIComponent(fileKey)}`;
    }
    return url;
  }

  /**
   * Get auth headers for download (exposed for external use)
   */
  async getDownloadHeaders(): Promise<Record<string, string>> {
    return this.getAuthHeadersWithOperator();
  }
}
